import {
  BrandIcon,
  IconSizeEnum,
  RelevantPeopleIcon,
  ScreenTextOcrIcon,
  STTIcon,
  Tooltip,
  VideoCutsCortesIcon,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { wrapperIconByStatus } from '@/helpers/wrapperIconStatus';
import { StatusEnrichmentEnum } from '@/types/types';

type Enrichments = {
  ocr?: boolean;
  peoples?: boolean;
  logos?: boolean;
  stt?: boolean;
  advertising?: boolean;
};

type StatusEnrichments = {
  ocr?: StatusEnrichmentEnum;
  peoples?: StatusEnrichmentEnum;
  logos?: StatusEnrichmentEnum;
  stt?: StatusEnrichmentEnum;
  advertising?: StatusEnrichmentEnum;
};

type RenderEnrichementsIconsProps = {
  iconSize?: IconSizeEnum;
  iconClassname?: string;
  classname?: string;
  enrichments?: Enrichments;
  statusEnrichments?: StatusEnrichments;
};

const wrapperTextByStatus = (text: string, statusText?: string) => {
  if (!statusText) return text;

  return `${text} - ${statusText}`;
};

export function RenderEnrichementsIcons({
  enrichments = {},
  statusEnrichments = {},
  iconSize = IconSizeEnum.SM,
  iconClassname,
  classname,
}: RenderEnrichementsIconsProps) {
  const { t } = useTranslation();

  const enrichmentList: (keyof Enrichments)[] = ['ocr', 'stt', 'logos', 'peoples', 'advertising'];
  const filteredEnrichments = enrichmentList.reduce(
    (acc, enrichment) => {
      if (enrichments[enrichment]) acc.push(enrichment);
      return acc;
    },
    [] as (keyof Enrichments)[]
  );

  const mapper = {
    ocr: {
      icon: ScreenTextOcrIcon,
      text: t('Texto en pantalla (OCR)'),
    },
    stt: {
      icon: STTIcon,
      text: t('Transcripción de texto'),
    },
    logos: {
      icon: BrandIcon,
      text: t('Localizar logos'),
    },
    peoples: {
      icon: RelevantPeopleIcon,
      text: t('Personas de interés'),
    },
    advertising: {
      icon: VideoCutsCortesIcon,
      text: t('Cortes automáticos'),
    },
  };

  const statusMapper: Record<
    string,
    {
      text: string;
      className: string;
    }
  > = {
    [StatusEnrichmentEnum.ERROR]: {
      text: t('Error'),
      className: 'bg-feedback-error-20',
    },
    [StatusEnrichmentEnum.PROCESSED]: {
      text: t('Procesado'),
      className: 'bg-extra-70',
    },
    [StatusEnrichmentEnum.PROCESSING]: {
      text: t('Procesando'),
      className: 'bg-extra-80 animate-fade-in-out duration-1000',
    },
    [StatusEnrichmentEnum.REPROCESSED]: {
      text: t('Reprocesado'),
      className: 'bg-extra-70',
    },
    [StatusEnrichmentEnum.REPROCESSING]: {
      text: t('Reprocesando'),
      className: 'bg-extra-80 animate-fade-in-out duration-1000',
    },
  };

  return (
    <div className={clsx('flex gap-1', classname)}>
      {filteredEnrichments.map(key => {
        const status = statusEnrichments[key];
        const { icon: Icon, text } = mapper[key];
        const { text: statusText, className: statusClassName } =
          statusMapper[status as StatusEnrichmentEnum] ?? {};

        return (
          <Tooltip key={key} help={wrapperTextByStatus(text, statusText)} grow>
            {wrapperIconByStatus(
              <Icon className={iconClassname} size={iconSize} />,
              statusClassName
            )}
          </Tooltip>
        );
      })}
    </div>
  );
}
